import { useState, useEffect, useCallback } from "react";
import { getAuthHeaders } from "../services/getAuthHeaders";
import { useUiLang } from "../../../context/UiLangContext";

export interface Profile {
  user_id: string;
  email: string;
  name: string;
}

interface UseProfileReturn {
  profile: Profile | null;
  loading: boolean;
  saving: boolean;
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  success: string;
  fetchProfile: () => Promise<void>;
  updateProfile: (name: string) => Promise<boolean>;
  resetState: () => void;
}

export function useProfile(userServiceUrl: string, isOpen: boolean): UseProfileReturn {
  const { uiLang } = useUiLang();
  const isEn = uiLang === "en";

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const resetState = useCallback(() => {
    setProfile(null);
    setError("");
    setSuccess("");
  }, []);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = userServiceUrl.replace(/\/$/, "");
      const targetUrl = `${cleanedUrl}/user/profile`;
      
      const res = await fetch(targetUrl, {
        method: "GET",
        headers: authHeaders,
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Failed to fetch profile: ${res.status} ${res.statusText}`
            : `Error al obtener perfil: ${res.status} ${res.statusText}`
        );
      }

      const data = await res.json();
      if (data.profile) {
        setProfile(data.profile);
      } else {
        throw new Error(isEn ? "No profile data returned." : "No se recibieron datos de perfil.");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Could not connect to profile service." : "No se pudo conectar al servicio de perfil."));
    } finally {
      setLoading(false);
    }
  }, [userServiceUrl, isEn]);

  const updateProfile = useCallback(async (name: string): Promise<boolean> => {
    if (!name.trim()) {
      setError(isEn ? "Name cannot be empty." : "El nombre no puede estar vacío.");
      return false;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const authHeaders = await getAuthHeaders();
      const cleanedUrl = userServiceUrl.replace(/\/$/, "");
      const targetUrl = `${cleanedUrl}/user/profile`;
      
      const res = await fetch(targetUrl, {
        method: "PATCH",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ name: name.trim() }),
      });

      if (!res.ok) {
        throw new Error(
          isEn
            ? `Failed to update profile: ${res.status} ${res.statusText}`
            : `Error al actualizar perfil: ${res.status} ${res.statusText}`
        );
      }

      setSuccess(isEn ? "Profile updated successfully!" : "¡Perfil actualizado con éxito!");
      setProfile((prev) => (prev ? { ...prev, name: name.trim() } : null));
      return true;
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isEn ? "Error updating profile." : "Error al actualizar el perfil."));
      return false;
    } finally {
      setSaving(false);
    }
  }, [userServiceUrl, isEn]);

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    } else {
      resetState();
    }
  }, [isOpen, fetchProfile, resetState]);

  return {
    profile,
    loading,
    saving,
    error,
    setError,
    success,
    fetchProfile,
    updateProfile,
    resetState,
  };
}
